/**
 * How far along an attachment's upload is — and nothing else.
 *
 * This is a separate module from `attachmentStorage.ts` on purpose. That one
 * imports `firebase/auth`, which touches global `fetch` AT IMPORT TIME; three
 * chat-isolation suites that render <ChatAttachment> have no `fetch` in their
 * environment and failed to load the moment the tile imported the uploader
 * (2026-09-11). Mocking `firebase/auth` in each of them would have hidden the
 * real point: a tile that wants to show a percentage should depend on a
 * PROGRESS READER, not on the thing that performs uploads.
 *
 * Keyed by File, and a WeakMap because it is only ever read by File identity
 * and never enumerated: a removed attachment's entry dies with the File, so
 * there is no cleanup path to forget to call.
 */
const progressByFile = new WeakMap<File, number>();

/** Record progress for a file currently uploading. Fraction, 0..1. */
export function setUploadProgress(file: File, fraction: number): void {
  if (!file || !Number.isFinite(fraction)) return;
  progressByFile.set(file, Math.max(0, Math.min(1, fraction)));
}

/**
 * Fraction uploaded (0..1), or undefined when this lane cannot report it —
 * the CF proxy lane sends the File through `fetch`, which has no progress
 * events, so "undefined" is the honest answer there and the caller shows the
 * label it always showed.
 */
export function getUploadProgress(file: File | undefined | null): number | undefined {
  return file ? progressByFile.get(file) : undefined;
}

/**
 * Waiting for its turn (2026-10-08). Large files upload a few at a time
 * (largeUploadLane LARGE_UPLOAD_SLOTS); one waiting behind others reports no
 * progress, and must not read as stalled to the tile that shows it.
 */
const queuedFiles = new WeakSet<File>();

export function setUploadQueued(file: File, queued: boolean): void {
  if (!file) return;
  if (queued) queuedFiles.add(file);
  else queuedFiles.delete(file);
}

export function isUploadQueued(file: File | undefined | null): boolean {
  return file ? queuedFiles.has(file) : false;
}

/**
 * ── IS ANY UPLOAD STILL MOVING? (2026-10-08, the 50 GB raise) ──────────────
 *
 * An upload lives in this tab. A reload ends it — and a 50 GB file takes about
 * an hour on a good line, so the things in the app that reload the tab on
 * their own (the update banner's refresh, the stale-tab self-heal) must hold
 * while one is under way, and closing the tab must ask first.
 *
 * "Moving" is deliberately not "exists": an upload that has had no progress
 * for UPLOAD_MOVING_FRESH_MS is not one a reload would rescue anything from,
 * and a provably broken tab must still be allowed to heal itself. The
 * uploader marks progress; this module only counts.
 */
export const UPLOAD_MOVING_FRESH_MS = 5 * 60_000;

interface UploadActivityRecord {
  lastProgressAt: number;
}

const activeUploads = new Map<number, UploadActivityRecord>();
let nextActivityId = 1;
const activityListeners = new Set<() => void>();

function notifyActivity(): void {
  for (const listener of Array.from(activityListeners)) {
    try {
      listener();
    } catch {
      // A listener's failure is its own; the others still hear.
    }
  }
}

export interface UploadActivity {
  /** Bytes moved (or the upload just got its turn). */
  progress(now?: number): void;
  /** The upload settled — succeeded, failed or was cancelled. Idempotent. */
  end(): void;
}

/** Register an upload that has started. The caller MUST call end() when it settles. */
export function beginUploadActivity(now: number = Date.now()): UploadActivity {
  const id = nextActivityId++;
  activeUploads.set(id, { lastProgressAt: now });
  notifyActivity();
  return {
    progress(at: number = Date.now()) {
      const rec = activeUploads.get(id);
      if (rec) rec.lastProgressAt = at;
    },
    end() {
      if (activeUploads.delete(id)) notifyActivity();
    },
  };
}

/** True while at least one upload has moved bytes within UPLOAD_MOVING_FRESH_MS. */
export function isAnyUploadMoving(now: number = Date.now()): boolean {
  for (const rec of Array.from(activeUploads.values())) {
    if (now - rec.lastProgressAt < UPLOAD_MOVING_FRESH_MS) return true;
  }
  return false;
}

/** How many uploads are registered (moving or not). */
export function activeUploadCount(): number {
  return activeUploads.size;
}

/** Hear when an upload starts or settles. Returns the unsubscribe. */
export function subscribeUploadActivity(listener: () => void): () => void {
  activityListeners.add(listener);
  return () => {
    activityListeners.delete(listener);
  };
}
